import { layoutFrame } from "./layoutFrame";
import { drawGeometry } from "./drawGeometry";
import { resolveLastPrice } from "./overlays/lastPrice";
import { resolveEnginePositions } from "./overlays/positions/positionsText";

/**
 * Draws one frame without the text layer: moves what is animated
 * (animator), lays the chart out, then draws the WebGL geometry (skipped
 * while the context is lost) and places the pulse ring of the Line dot.
 * The last price line follows the shown (moving) last bar; position lines
 * and zones are placed on the price scale of the frame. The returned
 * frame is what the text layer is drawn from (drawTextLayer).
 *
 * @param {object} engine - ChartEngine
 * @param {{ width: number, height: number, pixelRatio: number }} engine.size - CSS px
 * @param {object} engine.data - DataController: bars and the price precision
 * @param {object} engine.timeScale
 * @param {object} engine.priceScale
 * @param {object} engine.timeAxis
 * @param {number} engine.priceAxisWidth - width used in the previous frame
 * @param {object} engine.series - mainSeries
 * @param {object} engine.indicators - createIndicators
 * @param {object} engine.drawings - createDrawings
 * @param {object} engine.paneStack - places the oscillator panes
 * @param {object} engine.paneGridLines
 * @param {object} engine.animator
 * @param {object} engine.pulseRing
 * @param {object} engine.glLayer
 * @param {object} engine.gridLines
 * @param {object} engine.overlayLines
 * @param {object} engine.positionZones - rect renderer of the position zones
 * @param {object} engine.positions - positionsState (resolveEnginePositions)
 * @param {object} engine.textLayer
 * @param {{ colors: object, fontFamily: string }} engine.theme
 * @returns {{
 *   pane: object, plotHeight: number, indicatorPanes: object[], paneArea: object, range: { first: number, last: number },
 *   priceLabels: object[], priceAxisWidth: number, timeTicks: object[], lastPrice: object|null,
 * }} see layoutFrame
 */
export const drawFrame = (engine) => {
  const {
    size,
    data,
    timeScale,
    priceScale,
    timeAxis,
    priceAxisWidth,
    series,
    indicators,
    drawings,
    paneStack,
    paneGridLines,
    animator,
    pulseRing,
    glLayer,
    gridLines,
    overlayLines,
    positionZones,
    textLayer,
    theme,
  } = engine;
  const { store, priceFormat } = data;
  const { measure } = textLayer;
  const { fontFamily } = theme;

  animator.beginFrame();

  const layout = layoutFrame({
    size,
    store,
    series,
    indicators,
    paneStack,
    timeScale,
    priceScale,
    smoothPriceRange: animator.smoothPriceRange,
    priceAxisWidth,
    priceFormat,
    measure,
    fontFamily,
  });
  const { pane } = layout;
  const timeTicks = timeAxis.buildTicks({ range: layout.range, timeScale, measure, fontFamily });
  const { lastValue } = series;
  const lastPrice = resolveLastPrice(lastValue, priceScale, pane);

  if (glLayer.isReady) {
    const { scene: positionScene } = resolveEnginePositions(engine, pane);

    drawGeometry({
      glLayer,
      gridLines,
      series,
      indicators,
      drawings,
      paneGridLines,
      overlayLines,
      positionZones,
      positionScene,
      layout,
      timeTicks,
      timeScale,
      priceScale,
      pixelRatio: size.pixelRatio,
      theme,
      isSnappedX: !animator.isFollowingView,
    });
  }

  const hasDot = series.isLine && Boolean(lastValue);

  pulseRing.update(
    hasDot && animator.isMotionEnabled
      ? { x: timeScale.indexToX(lastValue.index), y: priceScale.priceToY(lastValue.price) }
      : null,
    pane,
  );

  animator.endFrame();

  return { ...layout, timeTicks, lastPrice };
};
